// Reactive application state as Svelte stores. These hold ONLY serializable
// data — live google.maps objects live in the marker-manager, keyed by id.
//
// Persistence keeps the exact legacy localStorage key + JSON shape so existing
// users restore seamlessly; friends/trips that predate stable ids get one
// assigned on load (migrateSaved).

import { writable, get, derived, type Writable, type Readable } from 'svelte/store';
import { householdSize } from './household';
import { DEFAULT_AMENITY_KEYS, resolveAmenityKeys } from './scoring';
import type {
  AmenityPrefs,
  Candidate,
  Friend,
  MapViewState,
  Suggestion,
  TravelMode,
  TravelTimeSettings,
  Trip,
} from './types';

const STORAGE_KEY = 'sfhome:state';
// Records which identity the locally-stored state belongs to: a Firebase uid
// when signed in, or 'anon' for the signed-out sandbox. We refuse to load state
// whose owner doesn't match the current identity, so a signed-in user's synced
// data can never leak into the signed-out experience (and vice versa).
const OWNER_KEY = 'sfhome:owner';
const ANON = 'anon';

// The identity that owns the current in-memory state + local writes. Mutated by
// applySignIn/applySignOut as auth changes; defaults to the anonymous sandbox.
let currentIdentity: string | null = null;

export type PlacementMode = 'none' | 'candidate' | 'friend' | 'trip';
export type AddCategory = 'friend' | 'favorite';

// ---- id generation ----
let idCounter = 0;
export function genId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter}_${Math.random().toString(36).slice(2, 7)}`;
}

// ---- stores ----
export const friends: Writable<Friend[]> = writable([]);
export const trips: Writable<Trip[]> = writable([]);
export const candidates: Writable<Candidate[]> = writable([]);
export const suggestions: Writable<Suggestion[]> = writable([]);
export const activeCandidateId: Writable<string | null> = writable(null);
export const currentMode: Writable<TravelMode> = writable('WALKING');
export const placementMode: Writable<PlacementMode> = writable('none');
/**
 * Name captured before the pin is dropped (the mobile add sheet asks for it up
 * front). null means "ask once the pin lands" — the sidebar's drop-pin buttons.
 */
export const placementHouseholdSize = writable(1);
export const friendPlacement = writable<{ lat: number; lng: number } | null>(null);
export const placementName: Writable<string | null> = writable(null);
/** Which quick-add form the mobile add sheet is showing, or null when closed. */
export const addSheet: Writable<Exclude<PlacementMode, 'none'> | null> = writable(null);
// Leaving placement mode always drops the pre-captured name so a later
// sidebar-initiated placement falls back to the name prompt.
placementMode.subscribe((mode) => {
  if (mode === 'none') placementName.set(null);
  if (mode !== 'friend') { friendPlacement.set(null); placementHouseholdSize.set(1); }
});
export const addCategory: Writable<AddCategory> = writable('friend');
export const travelTime: Writable<TravelTimeSettings> = writable({
  enabled: true,
  mode: 'WALKING',
  minutes: 15,
});
export const mapView: Writable<MapViewState | null> = writable(null);
/** Which amenities count toward the "Daily places" score. Persisted + synced. */
export const amenityPrefs: Writable<AmenityPrefs> = writable({ enabled: [...DEFAULT_AMENITY_KEYS] });
/** Resolved, validated set of enabled amenity keys (falls back to defaults). */
export const enabledAmenityKeys: Readable<Set<string>> = derived(amenityPrefs, (p) => resolveAmenityKeys(p.enabled));

export function setAmenityEnabled(key: string, on: boolean): void {
  amenityPrefs.update((p) => {
    const cur = resolveAmenityKeys(p.enabled);
    if (on) cur.add(key); else cur.delete(key);
    return { enabled: [...cur] };
  });
}
export function resetAmenityPrefs(): void {
  amenityPrefs.set({ enabled: [...DEFAULT_AMENITY_KEYS] });
}
/** True when the bundled walkthrough dataset is the current starting point. */
export const exampleLoaded: Writable<boolean> = writable(false);
/** Bundled example revision, used to refresh previously-loaded walkthroughs. */
export const exampleVersion: Writable<number> = writable(0);

// Raw "edit as text" projections of friends/trips (textarea contents).
export const friendsRaw: Writable<string> = writable('');
export const tripsRaw: Writable<string> = writable('');

// True once the Google map instance exists.
export const mapReady: Writable<boolean> = writable(false);

// ---- transient toast ----
/** Short confirmation shown over the map; '' when nothing is up. */
export const toast: Writable<string> = writable('');
let toastTimer: ReturnType<typeof setTimeout> | null = null;

export function showToast(message: string, ms = 2200): void {
  if (toastTimer) clearTimeout(toastTimer);
  toast.set(message);
  toastTimer = setTimeout(() => toast.set(''), ms);
}

// ---- persistence ----
interface SavedShape {
  exampleLoaded?: boolean;
  exampleVersion?: number;
  friendsRaw?: string;
  tripsRaw?: string;
  friends?: Partial<Friend>[];
  trips?: Partial<Trip>[];
  candidates?: Partial<Candidate>[];
  activeCandidate?: string | null;
  currentMode?: TravelMode;
  travelTime?: Partial<TravelTimeSettings>;
  mapView?: Partial<MapViewState> | null;
  amenityPrefs?: Partial<AmenityPrefs> | null;
}

interface BackupShape {
  app?: string;
  version?: number;
  exportedAt?: string;
  data?: SavedShape;
}

export function loadPersisted(): SavedShape | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedShape) : null;
  } catch {
    return null;
  }
}

/** Which identity the locally-stored state belongs to ('anon', a uid, or null if none). */
export function localOwner(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}

/** Wipe all user data from the stores (UI prefs like map view + travel mode stay). */
export function resetStores(): void {
  friends.set([]);
  trips.set([]);
  candidates.set([]);
  suggestions.set([]);
  activeCandidateId.set(null);
  friendsRaw.set('');
  tripsRaw.set('');
  exampleLoaded.set(false);
  exampleVersion.set(0);
}

/**
 * Switch to a signed-in identity: clear whatever was loaded, then restore this
 * user's local cache only if it actually belongs to them. Cloud sync then loads
 * the source-of-truth on top (see sync.ts).
 */
export function applySignIn(uid: string): void {
  currentIdentity = uid;
  resetStores();
  if (localOwner() === uid) {
    const saved = loadPersisted();
    if (saved) migrateSaved(saved);
  }
}

/**
 * Switch to the signed-out sandbox: clear the signed-in data from view AND disk
 * so it can't reappear on reload. Subsequent edits persist fresh under 'anon'.
 */
export function applySignOut(): void {
  currentIdentity = null;
  resetStores();
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(OWNER_KEY);
  } catch {
    /* ignore */
  }
}

/** Hydrate stores from saved data, assigning ids where the legacy schema lacked them. */
export function migrateSaved(saved: SavedShape): void {
  exampleLoaded.set(saved.exampleLoaded === true);
  exampleVersion.set(Number.isFinite(saved.exampleVersion) ? saved.exampleVersion as number : 0);
  if (Array.isArray(saved.friends)) {
    friends.set(
      saved.friends.map((f) => ({
        id: (f as Friend).id || genId('f'),
        name: f.name || 'Friend',
        householdSize: householdSize(f.householdSize),
        address: f.address || '',
        lat: f.lat as number,
        lng: f.lng as number,
        formattedAddress: f.formattedAddress,
      })),
    );
  }
  if (Array.isArray(saved.trips)) {
    trips.set(
      saved.trips.map((t) => ({
        id: (t as Trip).id || genId('t'),
        name: t.name || 'Place',
        address: t.address || '',
        lat: t.lat as number,
        lng: t.lng as number,
        formattedAddress: t.formattedAddress,
      })),
    );
  }
  if (Array.isArray(saved.candidates)) {
    candidates.set(
      saved.candidates.map((c, i) => ({
        id: (c as Candidate).id || genId('c'),
        name: c.name || `Candidate ${i + 1}`,
        address: c.address || '',
        lat: c.lat as number,
        lng: c.lng as number,
        color: c.color || '#c44536',
        results: c.results || {},
        amenities: c.amenities || null,
        trips: c.trips || {},
      })),
    );
  }
  if (saved.activeCandidate) activeCandidateId.set(saved.activeCandidate);
  if (saved.currentMode) currentMode.set(saved.currentMode);
  if (saved.travelTime) {
    travelTime.update((tt) => ({
      enabled: typeof saved.travelTime!.enabled === 'boolean' ? saved.travelTime!.enabled : tt.enabled,
      mode: saved.travelTime!.mode || tt.mode,
      minutes: Number.isFinite(saved.travelTime!.minutes) ? (saved.travelTime!.minutes as number) : tt.minutes,
    }));
  }
  if (saved.mapView
    && Number.isFinite(saved.mapView.lat)
    && Number.isFinite(saved.mapView.lng)
    && Number.isFinite(saved.mapView.zoom)) {
    mapView.set({
      lat: saved.mapView.lat as number,
      lng: saved.mapView.lng as number,
      zoom: saved.mapView.zoom as number,
    });
  }
  if (typeof saved.friendsRaw === 'string') friendsRaw.set(saved.friendsRaw);
  if (typeof saved.tripsRaw === 'string') tripsRaw.set(saved.tripsRaw);
  if (saved.amenityPrefs && Array.isArray(saved.amenityPrefs.enabled)) {
    amenityPrefs.set({ enabled: [...resolveAmenityKeys(saved.amenityPrefs.enabled.filter((k) => typeof k === 'string'))] });
  }
}

export function snapshot(): SavedShape {
  return {
    exampleLoaded: get(exampleLoaded),
    exampleVersion: get(exampleVersion),
    friendsRaw: get(friendsRaw),
    tripsRaw: get(tripsRaw),
    friends: get(friends).map((f) => ({
      id: f.id, name: f.name, address: f.address, lat: f.lat, lng: f.lng, formattedAddress: f.formattedAddress, householdSize: householdSize(f.householdSize),
    })),
    trips: get(trips).map((t) => ({
      id: t.id, name: t.name, address: t.address, lat: t.lat, lng: t.lng, formattedAddress: t.formattedAddress,
    })),
    candidates: get(candidates).map((c) => ({
      id: c.id, name: c.name, address: c.address, lat: c.lat, lng: c.lng,
      color: c.color, results: c.results, amenities: c.amenities, trips: c.trips,
    })),
    activeCandidate: get(activeCandidateId),
    currentMode: get(currentMode),
    travelTime: get(travelTime),
    mapView: get(mapView),
    amenityPrefs: get(amenityPrefs),
  };
}

function backupFileName(): string {
  const stamp = new Date().toISOString().slice(0, 10);
  return `livenearby-backup-${stamp}.json`;
}

function savedShapeFromBackup(value: unknown): SavedShape {
  if (!value || typeof value !== 'object') {
    throw new Error('Backup file must contain a JSON object.');
  }
  const backup = value as BackupShape;
  const saved = backup.data && typeof backup.data === 'object'
    ? backup.data
    : value as SavedShape;
  if (
    !Array.isArray(saved.friends) &&
    !Array.isArray(saved.trips) &&
    !Array.isArray(saved.candidates) &&
    typeof saved.friendsRaw !== 'string' &&
    typeof saved.tripsRaw !== 'string'
  ) {
    throw new Error('Backup file does not look like LiveNearby saved data.');
  }
  return saved;
}

export function downloadSavedData(): void {
  const backup: BackupShape = {
    app: 'livenearby.ai',
    version: 1,
    exportedAt: new Date().toISOString(),
    data: snapshot(),
  };
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = backupFileName();
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function importSavedData(file: File): Promise<void> {
  const raw = JSON.parse(await file.text()) as unknown;
  const saved = savedShapeFromBackup(raw);
  // Reactive stores update the map + UI live — NO reload. A reload would let
  // cloud sync re-load the old Firestore doc and clobber the import; instead the
  // localStorage persister + cloud writer both pick up these store changes, so
  // the import sticks (and syncs up for signed-in users).
  migrateSaved(saved);
  persist();
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;
let persistArmed = false;

/** Write the current state to localStorage (debounced). */
export function persist(): void {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot()));
      localStorage.setItem(OWNER_KEY, currentIdentity ?? ANON);
    } catch {
      /* localStorage may be unavailable or full */
    }
  }, 250);
}

/** Fires whenever any persisted store changes. Shared by localStorage + cloud sync. */
const persistedChanges = derived(
  [friends, trips, candidates, activeCandidateId, currentMode, travelTime, friendsRaw, tripsRaw, mapView, exampleLoaded, exampleVersion, amenityPrefs],
  (v) => v,
);

/** Subscribe to persisted-state changes, skipping the initial synchronous emit. */
export function subscribePersisted(cb: () => void): () => void {
  let first = true;
  return persistedChanges.subscribe(() => {
    if (first) {
      first = false;
      return;
    }
    cb();
  });
}

/**
 * Start auto-persisting to localStorage whenever any persisted store changes.
 * Call once after the initial hydration so the hydration itself doesn't save.
 */
export function startAutoPersist(): void {
  if (persistArmed) return;
  persistArmed = true;
  subscribePersisted(persist);
}

/** Toggle a placement mode on/off (clicking the active one exits to 'none'). */
export function togglePlacement(mode: Exclude<PlacementMode, 'none'>): void {
  placementMode.update((cur) => (cur === mode ? 'none' : mode));
}

/**
 * Enter placement mode from the add sheet, which has already done the asking —
 * friends preview their pin before confirming. An empty string counts as "asked":
 * the entity then takes its derived default name rather than re-prompting.
 */
export function startPlacement(mode: Exclude<PlacementMode, 'none'>, name?: string): void {
  friendPlacement.set(null);
  placementHouseholdSize.set(1);
  placementMode.set(mode);
  placementName.set(name?.trim() ?? '');
}

/** Keep the active tool selected after a successful add, with a fresh name. */
export function continuePlacement(mode: Exclude<PlacementMode, 'none'>): void {
  if (get(placementMode) !== mode) return;
  placementName.set(null);
  placementHouseholdSize.set(1);
  friendPlacement.set(null);
}

// ---- store update helpers ----
export function updateCandidate(id: string, patch: Partial<Candidate>): void {
  candidates.update((list) => list.map((c) => (c.id === id ? { ...c, ...patch } : c)));
}
export function updateFriend(id: string, patch: Partial<Friend>): void {
  friends.update((list) => list.map((f) => (f.id === id ? { ...f, ...patch } : f)));
}
export function updateTrip(id: string, patch: Partial<Trip>): void {
  trips.update((list) => list.map((t) => (t.id === id ? { ...t, ...patch } : t)));
}

export function clearSavedData(): void {
  if (!confirm('Clear saved friends, places, and candidates from this browser?')) return;
  resetStores();
  mapView.set(null);
  // Persist the empty state so cleared pins stay cleared on reload.
  localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot()));
  localStorage.setItem(OWNER_KEY, currentIdentity ?? ANON);
  location.reload();
}
